// @flow

import { selector } from 'lib/selectors';
import { jeMatchedLineNumbersSelector, isStatementSelector } from 'domain/journal/selectors';
import { rtlEnable as rtlEnabledSelector } from 'domain/env';
import { Map, List } from 'immutable';

export const textract = <T: Object>(state: { textract: T }): T => state.textract;
export const textractData = selector(textract, (textract) => textract?.data);
// indicates data coming from textract is of new shape
export const textractEnabledSelector = selector(
  textract,
  isStatementSelector,
  (textract, isStatement) => (textract.enabled && !isStatement) || false,
);
export const textractFieldsMappingSelector = selector(textract, (textract) => textract.fields);
export const textractFieldsMappingUniqueSelector = selector(textractFieldsMappingSelector, (fields) => {
  const res = fields
    ? Object.values(
        fields.reduce((a, b) => {
          if (!a[b.display_name]) a[b.display_name] = b;
          return a;
        }, {}),
      )
    : {};
  return res;
});
// fields mapping
export const textractLineItemsTableSelector = selector(textractData, (textract) => textract?.items_table || {});

export const textractLineItemsPolygonsSelector = (page: ?number) =>
  selector(textractLineItemsTableSelector, (table) => {
    return Object.entries(table).reduce((A, [column, v]) => {
      Object.entries(v).map(([row, cell]) => {
        if (!cell) {
          return A;
        }
        if (page) {
          if (cell.page === page) {
            A.push({ row, column, page: cell.page, id: cell.cell_id, Geometry: { Polygon: cell.polygon } });
          }
        } else {
          A.push({ row, column, page: cell.page, id: cell.cell_id, Geometry: { Polygon: cell.polygon } });
        }
      });
      return A;
    }, []);
  });

export const textractLineItemsTablelLastColumnSelector = selector(
  textractLineItemsTableSelector,
  rtlEnabledSelector,
  (table, rtl) => {
    // selects last column cells, doesnt respect jeView, only respects rtl
    // keys might be presented as [1,2,4,5,6,8] with empty columns
    const func = rtl ? Math.min : Math.max;
    return table ? table[func(...Object.keys(table))] : {};
  },
);

// returns first page with table
export const textractFirstTablePageSelector = selector(textractData, (textract) => textract?.tables_coords?.[0]?.Page);

export const textractCoordsTableByPageSelector = (page) =>
  selector(textractData, (textract) => {
    const table = textract?.tables_coords?.filter(({ Page }) => page === Page)[0];
    return table?.Geometry?.Polygon;
  });

export const textractLineItemsTablelLastColumnMatchedLinesSelector = selector(
  textractLineItemsTablelLastColumnSelector,
  jeMatchedLineNumbersSelector,
  textractLineItemsPolygonsSelector(),
  (column, matchedLines: Map<string, List<number>>, polygons) => {
    const polygonIds =
      polygons && column && Object.keys(column).length > 0
        ? matchedLines
            .map((lines) => lines.map((line) => column[line]))
            .map((items) => items.map((item) => item?.cell_id))
            .map((items, index1) => items.map((item, index) => polygons.find((poly) => poly.id === item)))
        : new Map();

    return polygonIds.toJS();
  },
);

// returns first line of table fields
export const textractLineItemsTableHeadersSelector = selector(textractLineItemsTableSelector, (lineItems) =>
  lineItems ? Object.values(lineItems).map((line) => line[0] || line[1]) : [],
);

export const textractLineItemsBoundTableHeadersSelector = selector(textractLineItemsTableHeadersSelector, (headers) =>
  headers
    ?.map(({ data, header }) => ({ key: data, header }))
    // non-empty header indicates field is already bound in brains
    ?.filter(({ header }) => header !== null),
);
export const textractTableFieldsMappingSelector = selector(textractLineItemsTableSelector, (lineItems) => {
  if (lineItems) {
    return Object.values(lineItems).reduce((acc, fields) => {
      // @to-do use header_row instead of 0
      // temp fix as currently endpoint sends matrix with either 0 or 1 first row index
      const field = fields[0] || fields[1];
      acc.push({
        name: field.data,
        bindingCell: field && field.polygon,
        id: field && field.cell_id,
        metadata: field.metadata,
      });

      return acc;
    }, []);
  }
  return [];
});
